import { router } from '@inertiajs/react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import DriveSyncLayout from '../../layouts/DriveSyncLayout';
import TowerHeader from './TowerHeader';

const UploadArea = styled(Paper)(({ theme, isDragActive }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  }
}));

const HiddenInput = styled('input')({
  display: 'none',
});

export default function Edit({ trip, state_options = [], errors = {} }) {
  // Função para limpar valores inválidos de tempo
  const cleanTimeValue = (value) => {
    if (!value || value === null || value === 'null' || value === '--:--') {
      return '';
    }

    // Se o valor contém um timestamp ISO, extrair apenas a parte HH:MM
    if (typeof value === 'string' && value.includes('T')) {
      try {
        const date = new Date(value);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (error) {
        console.error('Erro ao processar timestamp:', value, error);
        return '';
      }
    }

    return value;
  };

  const [formData, setFormData] = React.useState({
    estimated_arrival_at: cleanTimeValue(trip.estimated_arrival_at),
    finished_at: cleanTimeValue(trip.finished_at),
    package_count: trip.package_count || '',
    state: trip.state || '',
    truck_picture: null
  });
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);
  const [isDragActive, setIsDragActive] = React.useState(false);

  // Sync server errors with local state when props change
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
    }
  }, [errors]);

  const fileInputRef = React.useRef(null);

  const acceptedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp'
  ];

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const isValidFileType = (file) => {
    return acceptedTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith('.jpg') ||
      file.name.toLowerCase().endsWith('.jpeg') ||
      file.name.toLowerCase().endsWith('.png') ||
      file.name.toLowerCase().endsWith('.webp');
  };

  const handleFileSelect = (file) => {
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.truck_picture;
      return newErrors;
    });

    if (!file) return;

    if (!isValidFileType(file)) {
      setFormErrors(prev => ({
        ...prev,
        truck_picture: 'Por favor, selecione uma imagem válida (JPG, PNG, WEBP)'
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setFormErrors(prev => ({
        ...prev,
        truck_picture: 'A imagem deve ter no máximo 5MB'
      }));
      return;
    }

    setFormData(prev => ({ ...prev, truck_picture: file }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleInputChange = (field) => (event) => {
    if (field === 'truck_picture') {
      const file = event.target.files[0];
      handleFileSelect(file);
      return;
    }

    const value = event.target.value;

    // Para campos de tempo, limpar valores inválidos
    if (field === 'estimated_arrival_at' || field === 'finished_at') {
      const cleanValue = cleanTimeValue(value);
      setFormData(prev => ({ ...prev, [field]: cleanValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing (both client-side and server errors)
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleClearField = (field) => {
    setFormData(prev => ({ ...prev, [field]: '' }));
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Status é obrigatório
    if (!formData.state) {
      newErrors.state = 'Status é obrigatório';
    }

    // Validação básica para package_count (deve ser número se preenchido)
    if (formData.package_count && isNaN(Number(formData.package_count))) {
      newErrors.package_count = 'Quantidade de volumes deve ser um número';
    }

    // Validação de horários (formato HH:MM)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    // Validação para estimated_arrival_at
    if (formData.estimated_arrival_at) {
      const value = formData.estimated_arrival_at.trim();
      if (value !== '' && value !== '--:--' && !timeRegex.test(value)) {
        newErrors.estimated_arrival_at = 'Formato inválido. Use HH:MM';
      }
    }

    // Validação para finished_at
    if (formData.finished_at) {
      const value = formData.finished_at.trim();
      if (value !== '' && value !== '--:--' && !timeRegex.test(value)) {
        newErrors.finished_at = 'Formato inválido. Use HH:MM';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Sempre enviar campos trip, mesmo que vazios, para evitar ParameterMissing
      const estimatedArrival = (formData.estimated_arrival_at &&
        formData.estimated_arrival_at !== '--:--' &&
        formData.estimated_arrival_at.trim() !== '') ? formData.estimated_arrival_at : '';

      const finishedAt = (formData.finished_at &&
        formData.finished_at !== '--:--' &&
        formData.finished_at.trim() !== '') ? formData.finished_at : '';

      // Enviar campos sempre, mesmo que vazios
      formDataToSend.append('trip[estimated_arrival_at]', estimatedArrival);
      formDataToSend.append('trip[finished_at]', finishedAt);
      formDataToSend.append('trip[package_count]', formData.package_count || '');
      formDataToSend.append('trip[state]', formData.state);

      // Apenas adicionar truck_picture se houver arquivo
      if (formData.truck_picture) {
        formDataToSend.append('trip[truck_picture]', formData.truck_picture);
      }

      router.put(trip.update_url, formDataToSend, {
        preserveScroll: true,
        forceFormData: true,
        onError: (errors) => {
          setFormErrors(errors);
        },
        onFinish: () => {
          setLoading(false);
        }
      });
    } catch (err) {
      setFormErrors({ general: 'Erro inesperado ao salvar' });
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.visit('/towers');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <DriveSyncLayout>
      <TowerHeader />

      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '800px' } }}>
        {/* Header */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Editar Viagem
          </Typography>
        </Stack>

        {/* Error Alert */}
        {getValidErrors().length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Corrija os erros abaixo:
            </Typography>
            <ul style={{ margin: '8px 0 0 16px' }}>
              {getValidErrors().map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Trip Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocalShippingIcon color="primary" />
              <Typography variant="h6">
                Informações da Viagem
              </Typography>
            </Box>
            <Grid container spacing={12}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Transportadora</Typography>
                <Typography variant="body1">{trip.carrier_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Motorista</Typography>
                <Typography variant="body1">{trip.user_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Viagem #</Typography>
                <Typography variant="body1">{trip.sequence_indicator}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Typography variant="body1">{trip.status}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <AccessTimeIcon color="primary" />
              <Typography variant="h6">
                Horários e Informações
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              {/* Status */}
              <Grid container spacing={10} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Status
                  </Typography>
                  <FormControl fullWidth variant="outlined" size="medium">
                    <InputLabel id="state-label">Status</InputLabel>
                    <Select
                      labelId="state-label"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      label="Status"
                      error={!!formErrors.state}
                      disabled={loading}
                    >
                      {state_options.map((option) => (
                        <MenuItem key={option[1]} value={option[1]}>
                          {option[0]}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.state && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {formErrors.state}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>

              {/* Horários */}
              <Grid container spacing={10} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Previsão de chegada
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleClearField('estimated_arrival_at')}
                      sx={{ minWidth: 'auto', p: 1, fontSize: '0.75rem' }}
                    >
                      Limpar
                    </Button>
                  </Box>
                  <TextField
                    type="time"
                    value={formData.estimated_arrival_at}
                    onChange={handleInputChange('estimated_arrival_at')}
                    error={!!formErrors.estimated_arrival_at}
                    helperText={formErrors.estimated_arrival_at}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      width: '200px',
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Hora de chegada
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => handleClearField('finished_at')}
                      sx={{ minWidth: 'auto', p: 1, fontSize: '0.75rem' }}
                    >
                      Limpar
                    </Button>
                  </Box>
                  <TextField
                    type="time"
                    value={formData.finished_at}
                    onChange={handleInputChange('finished_at')}
                    error={!!formErrors.finished_at}
                    helperText={formErrors.finished_at}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      width: '200px',
                    }}
                  />
                </Grid>
              </Grid>

              {/* Quantidade de Volumes */}
              <Grid container spacing={10} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Quantidade de Volumes
                  </Typography>
                  <TextField
                    type="number"
                    value={formData.package_count}
                    onChange={handleInputChange('package_count')}
                    error={!!formErrors.package_count}
                    helperText={formErrors.package_count}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      width: '200px',
                    }}
                  />
                </Grid>
              </Grid>

              {/* Foto do Baú - Área de Upload */}
              <Grid fullWidth spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhotoCameraIcon color="primary" fontSize="small" />
                    <Typography variant="h6">
                      Foto do Baú
                    </Typography>
                  </Box>

                  {/* Current image preview if exists */}
                  {trip.truck_picture && !formData.truck_picture && (
                    <Card elevation={1} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <img
                            src={trip.truck_picture}
                            alt="Foto do baú atual"
                            style={{
                              width: '50%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                          />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Imagem atual do baú
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              component="a"
                              href={trip.truck_picture}
                              target="_blank"
                              sx={{ mt: 1 }}
                            >
                              Ver em tamanho real
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Upload Area */}
                  <UploadArea
                    isDragActive={isDragActive}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    elevation={1}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      {isDragActive ? 'Solte a imagem aqui' : 'Arraste uma imagem ou clique para selecionar'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Formatos aceitos: JPG, PNG, WEBP
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tamanho máximo: 5MB
                    </Typography>

                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleInputChange('truck_picture')}
                    />
                  </UploadArea>

                  {/* File Preview */}
                  {formData.truck_picture && (
                    <Card elevation={1} sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Nova Imagem Selecionada
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <AttachFileIcon color="primary" />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {formData.truck_picture.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(formData.truck_picture.size)} • {formData.truck_picture.type}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setFormData(prev => ({ ...prev, truck_picture: null }))}
                            disabled={loading}
                          >
                            Remover
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Error for truck_picture */}
                  {formErrors.truck_picture && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {formErrors.truck_picture}
                    </Alert>
                  )}
                </Grid>
              </Grid>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </DriveSyncLayout>
  );
} 